// One injury record. Used in both the global IR tab and the per-player
// Injuries sub-tab. Compact one-line layout with optional expanded notes.
// Countdown chip when projected return is ≤ 7 days out.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, FileText, Pencil } from 'lucide-react';
import type { InjuryWithPlayer } from '@/hooks/useInjuries';
import { useArchiveInjury } from '@/hooks/useInjuries';
import { AddInjuryModal } from '@/components/AddInjuryModal';
import { apiFormPost, apiGet } from '@/lib/api';
import { formatDob, daysFromTodayLabel } from '@/lib/formatters';

type InjuryAttachment = {
  id: string;
  injuryId: string;
  filename: string;
  r2Key: string;
  sizeBytes: number;
  mimeType: string;
  uploadedAt: number;
  uploadedBy: string | null;
};

type Props = {
  injury: InjuryWithPlayer;
  /** Show player name + link. False inside a player file's Injuries tab. */
  showPlayer?: boolean;
};

export function InjuryCard({ injury, showPlayer = true }: Props) {
  const [open, setOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [editing, setEditing] = useState(false);
  const [returnToPlayDate, setReturnToPlayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [resolveNotes, setResolveNotes] = useState('');
  const archive = useArchiveInjury();

  const playerName = injury.player
    ? injury.player.displayName ?? `${injury.player.firstName} ${injury.player.lastName}`
    : 'Unknown';
  const team = injury.player?.currentTeam ?? '';

  const eta = daysFromTodayLabel(injury.projectedReturnDate);
  const showCountdown = injury.status === 'active' && eta.days != null && eta.days <= 7 && eta.days >= 0;
  const isOverdue = injury.status === 'active' && eta.days != null && eta.days < 0;
  const sourceLabel = injury.createdBy === 'espn-injury-report' ? 'ESPN report' : null;
  const hasResearch = injury.notes?.includes('Public injury research:') ?? false;
  const needsVerification = injury.severity?.toLowerCase().includes('verify') ?? false;

  async function onResolve() {
    setArchiving(true);
    try {
      await archive.mutateAsync({
        id: injury.id,
        returnToPlayDate,
        notes: resolveNotes.trim() ? resolveNotes.trim() : undefined,
      });
      setShowResolve(false);
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-3 py-3 text-left hover:bg-bg sm:px-4"
      >
        <span className="mt-0.5 shrink-0 text-muted">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              {showPlayer && injury.player && (
                <Link
                  to={`/people/${injury.playerId}?tab=injuries`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[15px] font-semibold text-ink hover:underline"
                >
                  {playerName}
                </Link>
              )}
              {showPlayer && team && <span className="font-mono text-[11px] text-muted">{team}</span>}
              <span className="text-[14px] text-ink">{injury.injuryType}</span>
              {injury.severity && (
                <span className="font-mono text-[11px] text-muted">{injury.severity}</span>
              )}
              {sourceLabel && (
                <span className="rounded-full border border-bartlettGold/40 bg-bartlettGold/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
                  {sourceLabel}
                </span>
              )}
              {hasResearch && (
                <span className="rounded-full border border-border bg-white px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted">
                  research
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {injury.status === 'active' ? (
                <>
                  <span className="font-mono text-[11px] text-muted">
                    ETA {injury.projectedReturnDate ? formatDob(injury.projectedReturnDate) : 'TBD'}
                  </span>
                  {(showCountdown || isOverdue) && (
                    <span
                      className={[
                        'inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[11px]',
                        isOverdue
                          ? 'border-flag-critical/30 bg-flag-critical/5 text-flag-critical'
                          : 'border-flag-warn/30 bg-flag-warn/5 text-flag-warn',
                      ].join(' ')}
                    >
                      ⚠ {isOverdue ? 'overdue' : eta.label}
                    </span>
                  )}
                </>
              ) : (
                <span className="font-mono text-[11px] text-muted">
                  archived · returned {injury.returnToPlayDate ? formatDob(injury.returnToPlayDate) : '—'}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {open && (
        <div className="space-y-2 border-l-2 border-bartlett/15 bg-bg/40 px-6 py-3 text-[13px] text-ink">
          <Field label="Injury date">{formatDob(injury.injuryDate)}</Field>
          <Field label="Return watch">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono">
                {injury.projectedReturnDate ? formatDob(injury.projectedReturnDate) : 'TBD'}
              </span>
              {injury.status === 'active' && eta.days != null && (
                <span
                  className={[
                    'rounded-full border px-2 py-0.5 font-mono text-[11px]',
                    isOverdue || needsVerification
                      ? 'border-flag-critical/30 bg-flag-critical/5 text-flag-critical'
                      : showCountdown
                        ? 'border-flag-warn/30 bg-flag-warn/5 text-flag-warn'
                        : 'border-border bg-white text-muted',
                  ].join(' ')}
                >
                  {isOverdue ? 'overdue' : eta.label}
                </span>
              )}
              {needsVerification && (
                <span className="rounded-full border border-flag-critical/30 bg-flag-critical/5 px-2 py-0.5 font-mono text-[11px] text-flag-critical">
                  verify
                </span>
              )}
            </div>
          </Field>
          {injury.gameContext && <Field label="Context">{injury.gameContext}</Field>}
          {injury.doctor && <Field label="Doctor">{injury.doctor}</Field>}
          {injury.notes && (
            <Field label="Notes">
              <p className="whitespace-pre-wrap">{injury.notes}</p>
            </Field>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-control border border-border bg-white px-3 text-[12px] font-semibold text-ink hover:border-bartlett/40"
            >
              <Pencil size={13} />
              Edit
            </button>
            {injury.status === 'active' && (
              <button
                type="button"
                onClick={() => setShowResolve((v) => !v)}
                className="inline-flex h-8 items-center gap-1.5 rounded-control border border-border bg-white px-3 text-[12px] font-semibold text-ink hover:border-bartlett/40 disabled:opacity-50"
              >
                <Check size={13} />
                Resolve
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFiles((v) => !v)}
              className="inline-flex h-8 items-center gap-1.5 rounded-control border border-border bg-white px-3 text-[12px] font-semibold text-ink hover:border-bartlett/40"
            >
              <FileText size={13} />
              {showFiles ? 'Hide files' : 'Files'}
            </button>
          </div>
          {showFiles && <AttachmentPanel injuryId={injury.id} />}
          {editing && <AddInjuryModal injury={injury} onClose={() => setEditing(false)} />}
          {showResolve && injury.status === 'active' && (
            <form
              className="mt-3 space-y-3 rounded-control border border-border bg-white p-3"
              onSubmit={(e) => {
                e.preventDefault();
                void onResolve();
              }}
            >
              <label className="grid gap-1">
                <span className="font-mono text-[11px] uppercase tracking-wide text-muted">
                  Return to play
                </span>
                <input
                  type="date"
                  required
                  value={returnToPlayDate}
                  onChange={(e) => setReturnToPlayDate(e.target.value)}
                  className="h-9 rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett"
                />
              </label>
              <label className="grid gap-1">
                <span className="font-mono text-[11px] uppercase tracking-wide text-muted">
                  Final notes
                </span>
                <textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  rows={3}
                  className="rounded-control border border-border p-3 text-sm outline-none focus:border-bartlett"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowResolve(false)}
                  className="h-8 rounded-control border border-border px-3 text-[12px] font-semibold text-ink hover:bg-bg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={archiving}
                  className="h-8 rounded-control border border-bartlett bg-bartlett px-3 text-[12px] font-semibold text-white disabled:opacity-50"
                >
                  {archiving ? 'Resolving...' : 'Archive injury'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function AttachmentPanel({ injuryId }: { injuryId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const attachments = useQuery({
    queryKey: ['injury-attachments', injuryId],
    queryFn: () => apiGet<{ items: InjuryAttachment[]; total: number }>(`/api/injuries/${injuryId}/attachments`),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.set('file', file);
      return apiFormPost<{ attachment: InjuryAttachment }>(`/api/injuries/${injuryId}/attachments`, form);
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['injury-attachments', injuryId] });
    },
    onError: () => setError('Upload failed. Use a PDF under the configured file-size limit.'),
  });

  return (
    <div className="mt-3 space-y-3 rounded-control border border-border bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-wide text-muted">PDF attachments</div>
          <div className="mt-0.5 text-[12px] text-muted">Doctor notes, imaging summaries, and signed injury files.</div>
        </div>
        <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-control border border-bartlett bg-bartlett px-3 text-[12px] font-semibold text-white hover:bg-bartlett/95">
          <FileText size={13} />
          <span>{upload.isPending ? 'Uploading...' : 'Upload PDF'}</span>
          <input
            type="file"
            accept="application/pdf"
            className="sr-only"
            disabled={upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              if (file.type !== 'application/pdf') {
                setError('PDF only.');
                return;
              }
              upload.mutate(file);
            }}
          />
        </label>
      </div>
      {error && <p className="text-[12px] text-flag-critical">{error}</p>}
      {attachments.isLoading ? (
        <p className="text-[12px] text-muted">Loading files...</p>
      ) : attachments.data && attachments.data.items.length > 0 ? (
        <div className="overflow-hidden rounded-control border border-border">
          {attachments.data.items.map((file) => (
            <div key={file.id} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-ink">{file.filename}</div>
                <div className="font-mono text-[11px] text-muted">
                  {formatBytes(file.sizeBytes)} · {formatAttachmentTime(file.uploadedAt)}
                </div>
              </div>
              <span className="shrink-0 rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase text-muted">
                stored
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-control border border-dashed border-border bg-bg/50 px-3 py-2 text-[12px] text-muted">
          No files attached yet.
        </p>
      )}
    </div>
  );
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatAttachmentTime(value: number): string {
  return new Date(value * 1000).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted">{label}</span>
      <div>{children}</div>
    </div>
  );
}
